import React, { useCallback, useMemo } from "react"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { getEditSchedulerJobRoute, SUPPORT_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { callSchedulerJob, fetchSchedulerStatus, SupportSchedulerQuery } from "../schedulerApi"
import { useColumnFactory } from "../../../../../../core/api/useTableColumns"
import { SchedulerJob, JobStatus, JobCommand } from "../schedulerTypes"
import { MenuItem, Stack, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { JobStatusBadge } from "../components/JobStatusBadge"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"

export function SchedulerJobList() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const { data, isFetching, isError, isLoading } = useApiQuery(fetchSchedulerStatus, {
        queryName: SupportSchedulerQuery.SCHEDULER_STATUS,
        dependencies: {},
    })

    const { mutate } = useApiMutation(callSchedulerJob, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportSchedulerQuery.SCHEDULER_STATUS] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
        },
    })

    const columnFactory = useColumnFactory<SchedulerJob>()

    const columns = useMemo<MRT_ColumnDef<SchedulerJob>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("support-reports-health-scheduler-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-reports-health-scheduler-table-header-cron"),
                accessorKey: "cronExpression",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-reports-health-scheduler-table-header-last-run"),
                    accessorKey: "lastRun",
                },
                (row) => row.lastRun,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-reports-health-scheduler-table-header-next-run"),
                    accessorKey: "nextRun",
                },
                (row) => row.nextRun,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("support-reports-health-scheduler-table-header-status"),
                accessorKey: "status",
                Cell: ({ row }) => {
                    return <JobStatusBadge placement="table" status={row.original.status} />
                },
                filterSelectOptions: [
                    {
                        value: "ACTIVE",
                        text: l10n.getString("support-reports-health-scheduler-table-status-active"),
                    },
                    {
                        value: "INACTIVE",
                        text: l10n.getString("support-reports-health-scheduler-table-status-inactive"),
                    },
                    {
                        value: "PAUSED",
                        text: l10n.getString("support-reports-health-scheduler-table-status-paused"),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<SchedulerJob>; closeMenu: () => void }) => {
            const { status, id } = row.original

            return [
                <MenuItem
                    key={`edit-${id}`}
                    onClick={() => {
                        closeMenu()
                        navigate(getEditSchedulerJobRoute(id))
                    }}
                >
                    <LocalizedStrict id="support-reports-health-scheduler-table-action-edit">
                        <span>Edit</span>
                    </LocalizedStrict>
                </MenuItem>,

                status !== JobStatus.ACTIVE && (
                    <MenuItem
                        key={`start-${id}`}
                        onClick={() => {
                            mutate({ jobId: id, command: JobCommand.START })
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="support-reports-health-scheduler-table-action-start">
                            <span>Start</span>
                        </LocalizedStrict>
                    </MenuItem>
                ),

                status !== JobStatus.INACTIVE && (
                    <MenuItem
                        key={`stop-${id}`}
                        onClick={() => {
                            mutate({ jobId: id, command: JobCommand.STOP })
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="support-reports-health-scheduler-table-action-stop">
                            <span>Stop</span>
                        </LocalizedStrict>
                    </MenuItem>
                ),

                status !== JobStatus.PAUSED && (
                    <MenuItem
                        key={`pause-${id}`}
                        onClick={() => {
                            mutate({ jobId: id, command: JobCommand.PAUSE })
                            closeMenu()
                        }}
                    >
                        <LocalizedStrict id="support-reports-health-scheduler-table-action-pause">
                            <span>Pause</span>
                        </LocalizedStrict>
                    </MenuItem>
                ),
            ].filter(Boolean)
        },
        [navigate, mutate],
    )

    return (
        <>
            <LocalizedStrict id="support-reports-health-scheduler-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ADMIN_SCHEDULER}
                    title="Scheduler"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-health">
                            <Breadcrumb>Health</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-health-scheduler">
                            <Breadcrumb>Scheduler</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <Stack direction="column" marginBottom="3rem" spacing=".5rem">
                <Stack display="flex" alignItems="center" direction="row" spacing="1rem">
                    <LocalizedStrict id="support-reports-health-scheduler-page-subtitle-main-job">
                        <Typography>Main job:</Typography>
                    </LocalizedStrict>
                    <JobStatusBadge status={data?.scheduler.status} />
                </Stack>
                <Stack direction="row" spacing="1rem">
                    <LocalizedStrict id="support-reports-health-scheduler-page-subtitle-load-in-seconds">
                        <Typography>Load in seconds:</Typography>
                    </LocalizedStrict>
                    <Typography>{data?.scheduler.loadInSeconds}</Typography>
                </Stack>
            </Stack>
            <ApiQueryTable
                isLoading={isLoading}
                isFetching={isFetching}
                isError={isError}
                data={data?.scheduler.jobs ?? []}
                enableTableFooter={false}
                enableBottomToolbar={false}
                renderRowActionMenuItems={renderRowActionMenuItems}
                columns={columns}
                initialState={{ density: "spacious" }}
            />
        </>
    )
}
